import { MouseEvent, ReactElement, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import HistoryIcon from '@mui/icons-material/History'
import {
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import { useListThreads } from '@api/agent-runs/agent-runs'
import { useSnackbar } from '@ui/snackbar'
import { formatThreadTimestamp } from '../utils'

interface Props {
  projectId: string
  activeThreadId: string
  /**
   * While the agent works or a chat upload runs: switching would unmount the
   * thread's chat, which stops following the running turn and drops the
   * approval card an upload resumes the agent from.
   */
  disabled: boolean
  // `model` is the thread's latest model, to preselect it in the model select.
  onSelectThread: (threadId: string, model: string | null | undefined) => void
  onNewThread: () => void
}

/**
 * The project's earlier chats of this user, from our own thread store
 * (`GET /agent-runs/threads`), plus "Neuer Chat". CopilotKit's own thread
 * endpoints are in-memory and unscoped in the self-hosted runtime, so the
 * list comes from the API where threads are persisted per user and project.
 */
const ThreadMenu = ({
  projectId,
  activeThreadId,
  disabled,
  onSelectThread,
  onNewThread,
}: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const { data, isFetching } = useListThreads(
    { project_guid: projectId },
    {
      query: {
        // Fetched whenever the menu opens, so a thread started a moment ago
        // is listed without any cache bookkeeping.
        enabled: open,
        staleTime: 0,
        onError: (error: AxiosError) => {
          enqueueSnackbar(buildErrorMessage(error, 'Chats konnten nicht geladen werden'), {
            variant: 'error',
          })
        },
      },
    },
  )

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const threads = data?.threads ?? []

  return (
    <>
      <Tooltip
        title={disabled ? 'Chat wechseln, sobald der Assistent fertig ist' : 'Frühere Chats'}
      >
        {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
        <span>
          <IconButton
            size="small"
            onClick={handleOpen}
            disabled={disabled}
            data-cy="ai-chat-threads"
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          disabled={disabled}
          onClick={() => {
            handleClose()
            onNewThread()
          }}
          data-cy="ai-chat-new-thread"
        >
          <ListItemIcon>
            <AddCommentOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Neuer Chat</ListItemText>
        </MenuItem>
        <Divider />
        {isFetching && threads.length === 0 && (
          <MenuItem disabled>
            <CircularProgress size={14} sx={{ mr: 1 }} />
            <ListItemText>Wird geladen …</ListItemText>
          </MenuItem>
        )}
        {!isFetching && threads.length === 0 && (
          <MenuItem disabled>
            <ListItemText>Noch keine Chats in diesem Projekt</ListItemText>
          </MenuItem>
        )}
        {threads.map(thread => (
          <MenuItem
            key={thread.id}
            selected={thread.id === activeThreadId}
            disabled={disabled}
            onClick={() => {
              handleClose()
              onSelectThread(thread.id, thread.model)
            }}
            sx={{ maxWidth: 360 }}
          >
            <ListItemText
              primary={thread.title ?? 'Chat ohne Titel'}
              primaryTypographyProps={{ noWrap: true }}
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {formatThreadTimestamp(thread.updated_at)}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default ThreadMenu
